import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import {
  qk, useAbsences, useDevices, useHealth, useMeasurementStatus, useOverrides, usePresence, useRooms, useStateHistory, useStates, useWakes,
} from '../api/queries';
import type { Device, Mode, Override, OverrideStartIn, State } from '../api/types';
import { useAction } from '../components/feedback';
import { Async, Badge, Card, Field, ModeChip, ModePicker, PageHeader, Segmented } from '../components/ui';
import { byId, deviceName, groupByRoom, isRadiator, isSensor, roomName, stateFor } from '../lib/domain';
import { COMMAND_LABEL, MEASUREMENT_STATUS_LABEL, MODE_LABEL, REASON_LABEL, WAITING_LABEL } from '../lib/labels';
import { ago, dayLong, dayTime, duration, readingFreshness, time } from '../lib/time';

export function DashboardPage() {
  const health = useHealth();
  const [scope, setScope] = useState('home');
  const forceDevice = (id: string) => {
    setScope(`device:${id}`);
    document.getElementById('ov-scope')?.focus();
  };
  return (
    <>
      <PageHeader
        title="Tableau de bord"
        subtitle={dayLong(new Date().toISOString())}
        actions={health.data && (
          <>
            <Badge tone={health.data.status === 'ok' ? 'ok' : 'warn'}>Service {health.data.status === 'ok' ? 'opérationnel' : 'dégradé'}</Badge>
            <Badge tone={health.data.mqtt === 'available' ? 'ok' : health.data.mqtt === 'disabled' ? 'neutral' : 'bad'}>MQTT {health.data.mqtt === 'available' ? 'connecté' : health.data.mqtt === 'disabled' ? 'désactivé' : 'dégradé'}</Badge>
          </>
        )}
      />
      <StatusStrip />
      <div className="split">
        <div className="split-main">
          <RoomsBoard onForce={forceDevice} />
        </div>
        <aside className="split-side">
          <QuickOverride scope={scope} setScope={setScope} />
          <SensorsCard />
          <RecentHistory />
        </aside>
      </div>
    </>
  );
}

function StatusStrip() {
  const presence = usePresence();
  const wakes = useWakes('scheduled', 1);
  const active = useAbsences('active');
  const scheduled = useAbsences('scheduled');
  const m = useMeasurementStatus();
  const nextWake = wakes.data?.data[0];
  const absence = active.data?.[0] ?? scheduled.data?.[0];
  const mm = m.data && (m.data.status === 'scheduled' || m.data.status === 'running') ? m.data : null;

  return (
    <div className="stat-grid">
      <div className="stat">
        <div className="stat-label">Présence</div>
        <div className="stat-value">{presence.data ? (presence.data.present ? 'Présent' : 'Absent') : '…'}</div>
        <div className="stat-sub">
          {presence.data
            ? presence.data.sources_total === 0
              ? 'Aucune source : toujours présent'
              : `${presence.data.sources_present} source(s) sur ${presence.data.sources_total} à l’intérieur${presence.data.since ? ` · depuis ${dayTime(presence.data.since)}` : ''}`
            : ''}
        </div>
        <Link to="/presence" className="stat-link">Voir les sources</Link>
      </div>
      <div className="stat">
        <div className="stat-label">Prochain réveil</div>
        <div className="stat-value">{nextWake ? <><span>{dayTime(nextWake.scheduled_at).replace(/ · .*/, '')}</span> <span className="mono">{time(nextWake.scheduled_at)}</span></> : 'Aucun'}</div>
        <div className="stat-sub">
          {nextWake?.wake_phases?.length
            ? `Préchauffage dès ${time(nextWake.wake_phases.map((p) => p.preheat_start_at).sort()[0])}`
            : nextWake ? 'Aucune phase Réveil applicable' : 'Déclarez-en un depuis Réveil'}
        </div>
        <Link to="/evenements" className="stat-link">{nextWake ? 'Modifier ou annuler' : 'Déclarer un réveil'}</Link>
      </div>
      <div className="stat">
        <div className="stat-label">Absence longue</div>
        <div className="stat-value">{active.data?.length ? 'En cours' : scheduled.data?.length ? 'Programmée' : 'Aucune'}</div>
        <div className="stat-sub">{absence ? `${dayTime(absence.start)} → ${dayTime(absence.end)}` : 'Hors-gel partout pendant une absence'}</div>
        <Link to="/evenements" className="stat-link">Gérer les absences</Link>
      </div>
      <div className="stat">
        <div className="stat-label">Mesure de déperdition</div>
        <div className="stat-value">{mm ? MEASUREMENT_STATUS_LABEL[mm.status].label : 'Aucune prévue'}</div>
        <div className="stat-sub">{mm ? (mm.status === 'running' ? `Étape en cours · fin max ${time(mm.deadline_at)}` : dayTime(mm.scheduled_at)) : 'Test sur 10 h maximum'}</div>
        <Link to="/mesure" className="stat-link">Détails du test</Link>
      </div>
    </div>
  );
}

function hardwareLine(s: State | undefined): string {
  if (!s) return 'état inconnu';
  const parts: string[] = [];
  if (s.reported_relays) parts.push(`relais ${s.reported_relays.join('·')}`);
  if (s.reported_mode) parts.push(`lu ${MODE_LABEL[s.reported_mode]}`);
  if (s.reported_at) parts.push(ago(s.reported_at));
  if (s.online === false) parts.push('hors ligne');
  if (s.reported_stale) parts.push('mesure périmée');
  return parts.join(' · ') || 'aucune télémétrie';
}

export function DeviceStateRow({ d, s, onForce }: { d: Device; s: State | undefined; onForce?: () => void }) {
  const cmd = s ? COMMAND_LABEL[s.command_status] : COMMAND_LABEL.unknown;
  return (
    <div className="device-row">
      <div className="device-id">
        <Link to={`/radiateurs/${d.id}`} className="strong">{deviceName(d)}</Link>
        <div className="mono small muted">{d.client_id}</div>
      </div>
      <div className="device-mode">
        <div className="row gap-s wrap">
          <ModeChip mode={s?.requested_mode} />
          <span className="small muted">demandé</span>
          {d.suspended && <Badge tone="neutral">Suspendu</Badge>}
        </div>
        <div className="small">{s ? REASON_LABEL[s.reason] : '—'}</div>
        {s?.waiting_reason && <div className="small muted">En attente : {WAITING_LABEL[s.waiting_reason]}</div>}
      </div>
      <div className="device-hw">
        <Badge tone={s?.reported_stale && cmd.tone === 'ok' ? 'warn' : cmd.tone}>{cmd.label}</Badge>
        <div className="mono small muted">{hardwareLine(s)}</div>
        {s?.last_error && <div className="small text-bad">{s.last_error}</div>}
      </div>
      {onForce && <button type="button" className="btn" onClick={onForce}>Forcer</button>}
    </div>
  );
}

function RoomsBoard({ onForce }: { onForce: (deviceId: string) => void }) {
  const devices = useDevices();
  const rooms = useRooms();
  const states = useStates();
  const groups = useMemo(
    () => groupByRoom((devices.data ?? []).filter(isRadiator), rooms.data ?? []).filter((g) => g.devices.length > 0),
    [devices.data, rooms.data],
  );
  return (
    <section aria-labelledby="h-rooms" className="stack">
      <div className="row between baseline wrap">
        <h2 id="h-rooms" className="section-title">Radiateurs par pièce</h2>
        <span className="small muted">Mode demandé par le service · état remonté par le module</span>
      </div>
      <Async q={{ data: devices.data && rooms.data ? groups : undefined, isLoading: devices.isLoading || rooms.isLoading, error: devices.error ?? rooms.error }} empty={<p className="empty">Aucun radiateur. <Link to="/radiateurs">Ajouter un device</Link></p>}>
        {(gs) =>
          gs.map((g) => (
            <div key={g.room.id} className="card card-flush">
              <div className="card-band">
                <span className="strong">{g.room.name}</span>
                <span className="small muted">{g.devices.length} radiateur(s)</span>
              </div>
              {g.devices.map((d) => (
                <DeviceStateRow key={d.id} d={d} s={stateFor(states.data, d.id)} onForce={() => onForce(d.id)} />
              ))}
            </div>
          ))
        }
      </Async>
    </section>
  );
}

const DURATIONS = [30, 60, 120, 240, 480, 1440];

function QuickOverride({ scope, setScope }: { scope: string; setScope: (v: string) => void }) {
  const devices = useDevices();
  const rooms = useRooms();
  const overrides = useOverrides(true);
  const { run, busy } = useAction();
  const [mode, setMode] = useState<Mode>('confort');
  const [minutes, setMinutes] = useState(60);
  const absences = useAbsences('active');
  const roomMap = byId(rooms.data);
  const devMap = byId(devices.data);

  const label = (o: Override) =>
    o.scope === 'home' ? 'Domicile' : o.scope === 'room' ? `Pièce · ${roomName(roomMap, o.target_id)}` : `Radiateur · ${deviceName(devMap.get(o.target_id ?? ''))}`;

  const submit = () => {
    const [kind, id] = scope.split(':');
    const body: OverrideStartIn = { mode, duration_minutes: minutes, ...(kind === 'home' ? {} : { scope: kind === 'room' ? { room_id: id } : { device_id: id } }) };
    return run(() => api.send<Override>('POST', '/override', { body }).then((r) => r.data), {
      success: (o) => `Forçage ${MODE_LABEL[o.mode]} jusqu’à ${time(o.ends_at)}${o.cancelled_long_absence_ids?.length ? ' · absence longue annulée' : ''}`,
      invalidate: [['overrides'], qk.state, ['absences']],
    });
  };

  return (
    <Card title="Forçage manuel">
      <div className="stack">
        <Field label="Portée" htmlFor="ov-scope">
          <select id="ov-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="home">Domicile entier</option>
            {(rooms.data ?? []).map((r) => <option key={r.id} value={`room:${r.id}`}>Pièce · {r.name}</option>)}
            {(devices.data ?? []).filter(isRadiator).map((d) => <option key={d.id} value={`device:${d.id}`}>Radiateur · {deviceName(d)}</option>)}
          </select>
        </Field>
        <div className="field">
          <span className="label">Mode</span>
          <ModePicker value={mode} onChange={setMode} name="Mode du forçage" />
        </div>
        <div className="field">
          <span className="label">Durée</span>
          <Segmented label="Durée" value={minutes} onChange={setMinutes} options={DURATIONS.map((d) => ({ value: d, label: duration(d) }))} />
        </div>
        {absences.data?.length ? <p className="alert alert-warn small">Ce forçage annulera l’absence longue en cours, qui ne reprendra pas à la fin du forçage.</p> : <p className="small muted">Un nouveau forçage annule les absences longues en cours ; les absences futures sont conservées.</p>}
        <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={submit}>Appliquer le forçage</button>
        <div className="divider" />
        <div className="small strong">Forçages actifs</div>
        <Async q={{ data: overrides.data?.data, isLoading: overrides.isLoading, error: overrides.error }} empty={<p className="small muted">Aucun.</p>}>
          {(list) =>
            list.map((o) => (
              <div key={o.id} className="row between gap-s">
                <div className="small">
                  <ModeChip mode={o.mode} /> {label(o)}
                  <div className="mono muted">jusqu’à {dayTime(o.ends_at)}</div>
                </div>
                <button type="button" className="btn btn-s" disabled={busy}
                  onClick={() => run(() => api.send('DELETE', `/overrides/${o.id}`), { success: 'Forçage arrêté', invalidate: [['overrides'], qk.state] })}>
                  Arrêter
                </button>
              </div>
            ))
          }
        </Async>
      </div>
    </Card>
  );
}

function SensorsCard() {
  const devices = useDevices();
  const sensors = (devices.data ?? []).filter(isSensor);
  if (!devices.isLoading && sensors.length === 0) return null;
  return (
    <Card title="Sondes de température">
      <div className="stack">
        {sensors.map((d) => {
          const f = readingFreshness(d.last_reading?.measured_at);
          return (
            <div key={d.id} className="row between">
              <div>
                <div className="strong">{deviceName(d)}</div>
                <div className="small muted">
                  {d.suspended ? 'suspendue' : f === 'none' ? 'aucune mesure' : `${ago(d.last_reading?.measured_at)}${f === 'stale' ? ' · périmée' : f === 'lost' ? ' · perdue' : ''}`}
                </div>
              </div>
              <div className={`temp ${f === 'lost' || f === 'none' ? 'muted' : ''}`}>
                {d.last_reading ? `${d.last_reading.temperature_c.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} °C` : '—'}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function RecentHistory() {
  const h = useStateHistory(1, 6);
  const devices = useDevices();
  const map = byId(devices.data);
  return (
    <Card title="Dernières transitions" actions={<Link to="/reglages" className="small">Tout voir</Link>}>
      <Async q={{ data: h.data?.data, isLoading: h.isLoading, error: h.error }} empty={<p className="small muted">Aucune transition.</p>}>
        {(rows) => (
          <ul className="timeline-list">
            {rows.map((r) => (
              <li key={r.id}>
                <span className="mono muted">{time(r.occurred_at)}</span>
                <span>
                  <span className="strong">{deviceName(map.get(r.device_id ?? ''))}</span> → {MODE_LABEL[r.mode]}
                  <span className="block small muted">{REASON_LABEL[r.reason]}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Async>
    </Card>
  );
}
